/**
 * @fileoverview Context for managing game state and current game type.
 * 
 * This context handles the current game mode, question state, and game session
 * for the different game types (Truth or Dare, Would You Rather, Trivia).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { GameType, GameQuestion, GameSession } from '../types';
import { gameService } from '../services/gameService';
// Game data is now loaded from CDN, no need for gameDataService

import { useLanguage } from './LanguageContext';
import { useSettings } from './SettingsContext';
import { contentFilterService } from '../services/contentFilterService';

interface GameContextType {
  currentGameType: GameType;
  setCurrentGameType: (type: GameType) => void;
  currentQuestion: GameQuestion | null;
  setCurrentQuestion: (question: GameQuestion | null) => void;
  gameSession: GameSession | null;
  setGameSession: (session: GameSession | null) => void;
  isLoadingQuestion: boolean;
  getNextQuestion: () => Promise<void>;
  getTruthDareChoice: () => Promise<void>;
  clearGameState: () => void;
  questionHistory: GameQuestion[];
  addToHistory: (question: GameQuestion) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: React.ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [currentGameType, setCurrentGameType] = useState<GameType>(() => {
    return (localStorage.getItem('funModeGameType') as GameType) || 'just-spinner';
  });
  const [currentQuestion, setCurrentQuestion] = useState<GameQuestion | null>(null);
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [questionHistory, setQuestionHistory] = useState<GameQuestion[]>([]);
  
  const { currentLanguage } = useLanguage();
  const { showAdultSpinners } = useSettings();

  // Persist Fun Mode sub-menu selection to localStorage
  useEffect(() => {
    localStorage.setItem('funModeGameType', currentGameType);
  }, [currentGameType]);

  // Only preload when user actually starts playing
  const preloadGameQuestions = async () => {
    if (currentGameType === 'just-spinner') return;
    
    try {
      // All games now use CDN-based loading, no need for manual preloading
      await gameService.preloadQuestions(currentGameType, currentLanguage);
    } catch (error) {
      console.warn('Failed to preload game questions:', error);
    }
  };

  const getNextQuestion = useCallback(async () => {
    if (currentGameType === 'just-spinner') {
      setCurrentQuestion(null);
      return;
    }

    setIsLoadingQuestion(true);
    
    try {
      // All games now use CDN-based loading through their respective components
      // This function is kept for backward compatibility but no longer needed
      console.log(`🎮 [GameContext] getNextQuestion called for ${currentGameType} - CDN loading handled by components`);
      
      // For other game types, load from game service
      const questions = await gameService.getQuestions(currentGameType, currentLanguage, {
        includeAdult: showAdultSpinners,
        limit: 1
      });
      
      if (questions.length > 0) {
        setCurrentQuestion(questions[0]);
      }
      
      // Add to history
      if (currentQuestion) {
        setQuestionHistory(prev => [currentQuestion, ...prev.slice(0, 9)]);
      }
      
    } catch (error) {
      console.error('Error getting next question:', error);
      setCurrentQuestion(null);
    } finally {
      setIsLoadingQuestion(false);
    }
  }, [currentGameType, currentLanguage, showAdultSpinners, currentQuestion]);

  const getTruthDareChoice = useCallback(async () => {
    setIsLoadingQuestion(true);
    try {
      const choice = await gameService.getTruthDareChoice(currentLanguage, {
        includeAdult: showAdultSpinners
      });
      
      if (choice?.question) {
        setCurrentQuestion(choice.question);
        addToHistory(choice.question);
      }
    } catch (error) {
      console.error('Error loading truth/dare choice:', error);
    } finally {
      setIsLoadingQuestion(false);
    }
  }, [currentLanguage, showAdultSpinners]);

  const addToHistory = useCallback((question: GameQuestion) => {
    setQuestionHistory(prev => [question, ...prev.slice(0, 9)]); // Keep last 10 questions
  }, []);

  const clearGameState = useCallback(() => {
    setCurrentQuestion(null);
    setGameSession(null);
    setQuestionHistory([]);
  }, []);

  const value: GameContextType = {
    currentGameType,
    setCurrentGameType,
    currentQuestion,
    setCurrentQuestion,
    gameSession,
    setGameSession,
    isLoadingQuestion,
    getNextQuestion,
    getTruthDareChoice,
    clearGameState,
    questionHistory,
    addToHistory
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}; 